import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import { get, has, orderBy } from 'lodash';
import withData from '@bd/bdcore-data';
import SchoolList from '@bd/bdui-schoollist';
import Map from '@bd/bdui-coremap';
import { css } from '@bd/bdui-styles/emotion';
import { buildThumbnailUrl, GoogleMapUrl, SanitizeHtml, Truncate, SmoothLink } from '@bd/bdcore-utilities';
import LightBox from '@bd/bdui-lightbox';
import { Link } from '@bd/bdcore-named-routes';
import PageHelmet from '@bd/bdcore-pagehelmet';


import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CtaBanner from '../../components/CtaBanner';
import TestimonialBanner from '../../components/TestimonialBanner';
import ContactModal from '../../components/ContactModal';
import HomeCard from '../HomesOriginal/HomeCard';
import ShareThis from '../../components/ShareThis';
import HomeAgent from './HomeAgent';

function HomeDetail({ home, community, homes, plan }) {
  const otherHomes = homes.filter(otherHome => otherHome._id !== home._id);
  const background = {};
  if (has(home, 'photos[0].contentUrl')) {
    background.backgroundImage = buildThumbnailUrl(get(home, 'photos[0].contentUrl'), { width: 1000, height: 1000 });
  } else {
    background.backgroundImage = '/images/comming-soon.jpg';
  }
  return (
    <div className="homeDetail">
      <PageHelmet home={home} />
      <Header />
      <div>
        <div className="detail-header">
          <div className="container">
            <div>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link name="index">Home</Link>
                  </li>
                  {
                    get(community, 'name') &&
                    <li>Community: <Link name="community-detail" community={community}>{get(community, 'name')}</Link></li>
                  }
                  <li className="breadcrumb-item">
                    <Link
                      name="homes"
                      keepParams
                    >
                      {get(home, 'address.addressLocality')}
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {`${get(home, 'address.streetAddress')} ${get(home, 'address.addressLocality')}, ${get(home, 'address.addressRegion')} ${get(home, 'address.postalCode')}`}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="row detail-header-inner">
              <div className="col-md-5 detail-header-content">
                <h1>{get(home, 'address.streetAddress')}</h1>
                <h2>{`${get(home, 'address.addressLocality')}, ${get(home, 'address.addressRegion')} ${get(home, 'address.postalCode')}`} | <strong>{numeral(get(home, 'price')).format('$0,0')}</strong></h2>
                <ul className="detail-header-list2">
                  <li>
                    <img src="/images/icon-beds-white.svg" alt="Bed Icon" /><br />
                    <strong>{get(home, 'beds')}</strong><br />Beds
                  </li>
                  <li>
                    <img src="/images/icon-baths-white.svg" alt="Bath Icon" /><br />
                    <strong>{get(home, 'bathsFull')} {get(home, 'bathsHalf', 0) > 0 && '.5'}</strong><br />Baths
                  </li>
                  <li>
                    <img src="/images/icon-sqft-white.svg" alt="Square Feet Icon" /><br />
                    <strong>{numeral(get(home, 'sqft')).format('0,0')}</strong><br />SQ FT
                  </li>
                  {
                   !!get(home, 'stories', get(plan, 'stories')) &&
                   <li>
                     <img src="/images/icon-sqft-white.svg" alt="Square Feet Icon" /><br />
                     <strong>{get(home, 'stories', get(plan, 'stories'))}</strong><br />Stories
                    </li>
                  }
                  {
                   !!get(home, 'garages', get(plan, 'garages')) &&
                   <li>
                     <img src="/images/icon-sqft-white.svg" alt="Square Feet Icon" /><br />
                     <strong>{get(home, 'garages', get(plan, 'garages'))}</strong><br />Garages
                   </li>
                  }
                </ul>
                <h3>Status: {get(home, 'status')}</h3>
                <h3>Community: <Link name="community-detail" community={community}><span>{get(community, 'name')}</span></Link></h3>
                {
                  has(plan, 'name') &&
                  <h3>Plan: <Link name="plan-detail" plan={plan}><span>{get(plan, 'name')}</span></Link></h3>
                }
                <h3>Lot: {get(home, 'lot')}</h3>
                <ul className="detail-header-list1">
                  {
                    homes.length > 0 &&
                    <li><SmoothLink href="#details">Description</SmoothLink></li>
                  }
                  {
                    home.floorplanPhotos.length > 0 &&
                    <li><LightBox
                      items={get(home, 'floorplanPhotos', [])}
                      button={(
                        <a>Floor Plan</a>
                      )}
                    /></li>
                  }
                  {
                    has(home, 'tourUrl') &&
                    <li className="d-none d-md-inline-block"><SmoothLink href="#virtual">Virtual Tour</SmoothLink></li>
                  }
                  <li className="d-none d-md-inline-block"><SmoothLink href="#map">Map & Directions</SmoothLink></li>
                  <li className="d-none d-md-inline-block"><ShareThis>Share This Page</ShareThis></li>
                  <li><ContactModal className="btn btn-hollow ml-auto">Schedule A Showing</ContactModal></li>
                </ul>
              </div>
              <div className="col-md-7 detail-header-media" style={{ backgroundImage: `url('${background.backgroundImage}')` }}>
                <LightBox
                  items={get(home, 'photos', [])}
                  button={(
                    <a className="detail-media-photo-counter">
                      <img src="/images/icon-camera.svg" alt="Camera Icon" /><br />
                      {get(home, 'photos', []).length}
                    </a>
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="detail-content" id="details">
          <div className="container">
            <div className="row">
              {
                get(home, 'description') &&
                <div className="col-12 col-lg-6">
                  <h4>About this Home</h4>
                  <p className="big-p" dangerouslySetInnerHTML={SanitizeHtml(get(home, 'description'))} />
                </div>
              }
              <div className="col-12 col-lg-5 ml-auto">
                <h4>Learn About {' '}</h4>
                <img src={get(community, 'photos[0].contentUrl')} alt="Area" />
                {
                  has(community, 'name') &&
                  <h5>{get(community, 'name')}</h5>
                }
                {
                  has(community, 'description') &&
                  <Truncate
                    words={50}
                    value={get(community, 'description')}
                    button="Read More"
                    ellipse="..."
                  />
                }
                <Link name="community-detail" community={community} className="btn">Visit Community</Link>
              </div>
            </div>
          </div>
        </div>

        {
          get(home, 'photos', []).length > 0 &&
          <div className="CommunityGallery_wrapper" id="photos">
            <div className="container">
              <h3 className="underdog">Photos</h3>
              <LightBox items={get(home, 'photos', [])} >
                <ul className="PhotoList_list">
                  {
                    get(home, 'photos').map((photo, index) => {
                      return (
                        <li className="PhotoList_item" key={photo.contentUrl} data-index={index}>
                          <span data-index={index}>
                            {
                              photo.caption &&
                              <div className="PhotoList_imageTitle" data-index={index}>
                                {photo.caption}
                              </div>
                            }
                            {
                              photo &&
                              <div className="PhotoList_image" style={{ backgroundImage: `url('${buildThumbnailUrl(photo.contentUrl, { width: 400, height: 400 })}')` }} data-index={index} />
                            }
                          </span>
                        </li>
                      );
                    })
                  }
                </ul>
              </LightBox>
            </div>
          </div>
        }

        {
          !!get(home, 'tourUrl') &&
          <div className="detail-virtual-tour" id="virtual">
            <div className="container">
              <h4>Virtual Tour</h4>
              <div className="row">
                <div className="col-12 col-lg-8 mx-auto">
                  <img src="/images/detail-virtual-tour-photo-fpo.jpg" alt="Virtual Tour" />
                  <LightBox
                    items={[
                      {
                        element: (
                          <div
                            style={{
                              width: '800px',
                              maxWidth: '80%',
                              margin: '-225px auto 0 auto',
                              top: '50%',
                            }}
                            className="embed-responsive embed-responsive-16by9"
                          >
                            <iframe
                              src={get(home, 'tourUrl')}
                              frameBorder="0"
                              title="Matterport Tour"
                            />
                          </div>
                        ),
                      },
                    ]}
                    button={(
                      <div className="video-btn clickable" />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="background" />
          </div>
        }

        <div className="detail-map-section" id="map">
          <div className="container">
            <div className="row">
              <div className="col-12 col-lg-9">
                <div className="row">
                  <div className="col-12 col-lg-7 pr-0 map">
                    <Map
                      className={css`
                        height: 100%;
                        .leaflet-container {
                          height: 100%;
                        }
                        `}
                      markers={[home]}
                      pin={{
                        iconUrl: '/images/icon-map-pin-blue.svg',
                        iconSize: [21, 30],
                        iconAnchor: [11, 30],
                        popupAnchor: [0, -35],
                      }}
                      pins={[
                        {
                          iconUrl: '/images/map-pin-blue.svg',
                          when: marker => marker.type === 'salesOffice',
                        },
                      ]}
                    />
                  </div>
                  <div className="col-12 col-lg-5 px-0 directions-box">
                    <h4>Driving Directions</h4>
                    <p>
                      {get(community, 'directions')}
                    </p>
                    <p>
                      <a href={GoogleMapUrl(home, 16)} rel="noopener noreferrer" target="_blank">
                        <img src="/images/icon-map-pin-blue.svg" alt="Map Pin" /> Map This Home
                      </a>
                    </p>
                    {
                      get(community, 'outOfCommunity.checked') &&
                      <div>
                        <h4>Sales Office</h4>
                        <p>
                          {get(community, 'outOfCommunity.address.streetAddress')} <br />
                          {get(community, 'outOfCommunity.address.addressLocality')},{' '}
                          {get(community, 'outOfCommunity.address.addressRegion')}{' '}
                          {get(community, 'outOfCommunity.address.postalCode')}
                        </p>
                        <p>
                          <a href={GoogleMapUrl(get(community, 'outOfCommunity'), 16)} rel="noopener noreferrer" target="_blank">
                            <img src="/images/map-pin-blue.svg" alt="Map Pin" /> Map This Sales Office
                          </a>
                        </p>
                      </div>
                    }
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-3 pl-4">
                <div className="HomeDetails_school">
                  <h4>Area Schools</h4>
                  <SchoolList schools={get(community, 'schools', [])} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <HomeAgent />

        {
          otherHomes.length > 0 &&
          <div className="additional-homes" id="homes">
            <div className="full-width-headline">
              <h3>More Homes in This Community</h3>
              <div className="lines" />
            </div>
            <div className="list-background-color">
              <div className="container">
                <div className="row HomeCardsList Vertical">
                  {
                    orderBy(otherHomes, 'address.streetAddress')
                      .map(otherHome => (
                        <div className="col-sm-12 col-md-6 col-lg-4 col-xl-3 mb-3" key={otherHome._id}>
                          <HomeCard home={otherHome} />
                        </div>
                      ))
                  }
                </div>
              </div>
            </div>
          </div>
        }

      </div>
      <CtaBanner />
      <TestimonialBanner />
      <Footer />
    </div>
  );
}

HomeDetail.propTypes = {
  home: PropTypes.object,
  community: PropTypes.object,
  plan: PropTypes.object,
  homes: PropTypes.arrayOf(PropTypes.object),
};

HomeDetail.defaultProps = {
  home: undefined,
  community: undefined,
  plan: undefined,
  homes: [],
};

export default withData(
  {
    home: true,
    community: true,
    communities: true,
    homes: true,
    plan: true,
  },
  {
    required: {
      home: true,
    },
    ifMissing404: true,
  },
)(HomeDetail);



// WEBPACK FOOTER //
// ./src/pages/Homes/HomeDetail.js